/**
 * Auth soyutlama katmani.
 *
 * Clerk yapilandirilmamissa (VITE_CLERK_PUBLISHABLE_KEY yok) `ClerkProvider`
 * render sirasinda exception firlatir ve TUM uygulamayi (giris gerektirmeyen
 * herkese acik sayfalar dahil) cokertir.
 *
 * Bu modul tek giris noktasi olarak calisir: Clerk varsa gercek primitifleri,
 * yoksa "her zaman cikis yapmis" davranan guvenli stub'lari export eder.
 * Boylece arama/listeleme/fiyatlandirma gibi herkese acik akislar auth
 * saglayicisi olmadan da calisir - hem E2E testleri hem de Clerk'in gecici
 * olarak erisilemez oldugu durumlar icin onemli.
 */
import React from 'react';
import {
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  SignIn as ClerkSignIn,
  SignUp as ClerkSignUp,
  UserButton as ClerkUserButton,
  UserProfile as ClerkUserProfile,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from '@clerk/clerk-react';

export const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;

/** Clerk kullanilabilir mi? Modul yuklenirken bir kez hesaplanir. */
export const isAuthEnabled = Boolean(CLERK_PUBLISHABLE_KEY);

/* ------------------------------------------------------------------ */
/* Stub'lar - auth devre disiyken                                      */
/* ------------------------------------------------------------------ */

const noAuth = {
  isLoaded: true,
  isSignedIn: false as const,
  userId: null,
  sessionId: null,
  orgId: null,
  getToken: async () => null,
  signOut: async () => {},
};

const noUser = { isLoaded: true, isSignedIn: false as const, user: null };

function AuthDisabledNotice({ title }: { title: string }) {
  return (
    <div className="yd-card p-6 text-center" data-testid="auth-disabled-notice">
      <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
      <p className="mt-2 text-sm text-ink-500">
        Kimlik dogrulama bu ortamda yapilandirilmamis.
        <br />
        <code className="text-xs">VITE_CLERK_PUBLISHABLE_KEY</code> degerini tanimlayin.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export const useAuth: typeof useClerkAuth = isAuthEnabled
  ? useClerkAuth
  : ((() => noAuth) as unknown as typeof useClerkAuth);

export const useUser: typeof useClerkUser = isAuthEnabled
  ? useClerkUser
  : ((() => noUser) as unknown as typeof useClerkUser);

export const SignedIn: React.FC<{ children?: React.ReactNode }> = isAuthEnabled
  ? ClerkSignedIn
  : () => null;

export const SignedOut: React.FC<{ children?: React.ReactNode }> = isAuthEnabled
  ? ClerkSignedOut
  : ({ children }) => <>{children}</>;

export const UserButton: React.FC<Record<string, unknown>> = isAuthEnabled
  ? (ClerkUserButton as React.FC<Record<string, unknown>>)
  : () => null;

export const SignIn: React.FC<Record<string, unknown>> = isAuthEnabled
  ? (ClerkSignIn as React.FC<Record<string, unknown>>)
  : () => <AuthDisabledNotice title="Giris yapilamiyor" />;

export const SignUp: React.FC<Record<string, unknown>> = isAuthEnabled
  ? (ClerkSignUp as React.FC<Record<string, unknown>>)
  : () => <AuthDisabledNotice title="Kayit olunamiyor" />;

export const UserProfile: React.FC<Record<string, unknown>> = isAuthEnabled
  ? (ClerkUserProfile as React.FC<Record<string, unknown>>)
  : () => <AuthDisabledNotice title="Profil goruntulenemiyor" />;
